/**
 * FamiliarisationSettingsModal - Configure custom familiarisation requirements
 * Allows companies to add their own checklist items beyond regulatory minimums
 */

import React, { useState, useEffect } from 'react';
import { X, Plus, Trash2, Save, AlertCircle } from 'lucide-react';
import { useStorageState } from '../../../../hooks/useStorageState';

const FamiliarisationSettingsModal = ({ isOpen, onClose }) => {
  const { value: customItems = [], setValue: setCustomItems } = useStorageState('customFamiliarisationItems', []);
  const { value: familiarisationSettings = {}, setValue: setFamiliarisationSettings } = useStorageState('familiarisationSettings', {});
  const [editingItems, setEditingItems] = useState([]);
  const [validityPeriod, setValidityPeriod] = useState(12);
  const [newItem, setNewItem] = useState({ section: '', text: '', description: '', priority: 'normal' });
  const [showAddForm, setShowAddForm] = useState(false);

  // Available sections to add custom items to
  const sections = [
    { id: 'vessel_operations', label: 'Vessel & Operations' },
    { id: 'safety_procedures', label: 'Safety & Emergency Procedures' },
    { id: 'equipment_systems', label: 'Equipment & Systems' },
    { id: 'regulations_compliance', label: 'Regulations & Compliance' },
    { id: 'company_specific', label: 'Company Specific Requirements' }
  ];

  useEffect(() => {
    if (isOpen) {
      setEditingItems([...customItems]);
      setValidityPeriod(familiarisationSettings.validityPeriodMonths || 12);
    }
  }, [isOpen, customItems, familiarisationSettings]);

  const handleAddItem = () => {
    if (!newItem.section || !newItem.text) {
      alert('Please select a section and enter item text');
      return;
    }

    const item = {
      id: `custom_${Date.now()}`,
      ...newItem,
      isCustom: true,
      createdAt: new Date().toISOString()
    };

    setEditingItems([...editingItems, item]);
    setNewItem({ section: '', text: '', description: '', priority: 'normal' });
    setShowAddForm(false);
  };

  const handleRemoveItem = (itemId) => {
    setEditingItems(editingItems.filter(item => item.id !== itemId));
  };

  const handleSave = async () => {
    await setCustomItems(editingItems);
    await setFamiliarisationSettings({
      ...familiarisationSettings,
      validityPeriodMonths: validityPeriod
    });
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="fixed inset-0 bg-black bg-opacity-25" onClick={onClose} />
      
      <div className="relative min-h-screen flex items-center justify-center p-4">
        <div className="relative bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
          {/* Header */}
          <div className="bg-white border-b border-gray-200 px-6 py-4">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Familiarisation Settings</h2>
                <p className="text-sm text-gray-600 mt-1">Add custom checklist items for your company requirements</p>
              </div>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-500"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="overflow-y-auto max-h-[calc(90vh-200px)] p-6">
            {/* Validity Period Setting */}
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 mb-6">
              <h3 className="text-sm font-medium text-slate-900 mb-3">Familiarisation Validity</h3>
              <div className="flex items-center gap-4">
                <label className="text-sm text-slate-600">
                  Familiarisation records are valid for:
                </label>
                <select
                  value={validityPeriod}
                  onChange={(e) => setValidityPeriod(parseInt(e.target.value))}
                  className="px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                >
                  <option value={6}>6 months</option>
                  <option value={12}>12 months (default)</option>
                  <option value={18}>18 months</option>
                  <option value={24}>24 months</option>
                  <option value={36}>36 months</option>
                </select>
              </div>
              <p className="text-xs text-slate-500 mt-2">
                Crew will show as "Familiarisation Overdue" after this period.
              </p>
            </div>

            {/* Info Banner */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <div className="flex">
                <AlertCircle className="h-5 w-5 text-blue-400 mt-0.5" />
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-blue-800">Custom Requirements</h3>
                  <p className="text-sm text-blue-700 mt-1">
                    These items will be added to all crew familiarisation checklists in addition to the regulatory requirements.
                    Custom items can be marked as N/A if not applicable to specific crew members.
                  </p>
                </div>
              </div>
            </div>

            {/* Current Custom Items */}
            <div className="mb-6">
              <h3 className="text-base font-medium text-gray-900 mb-4">Current Custom Items</h3>
              {editingItems.length === 0 ? (
                <p className="text-sm text-gray-500 italic">No custom items added yet</p>
              ) : (
                <div className="space-y-3">
                  {editingItems.map((item) => {
                    const section = sections.find(s => s.id === item.section);
                    return (
                      <div key={item.id} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <p className="text-sm font-medium text-gray-900">{item.text}</p>
                            {item.description && (
                              <p className="text-sm text-gray-600 mt-1">{item.description}</p>
                            )}
                            <div className="flex items-center gap-4 mt-2">
                              <span className="text-xs text-gray-500">
                                Section: {section?.label || item.section}
                              </span>
                              {item.priority === 'critical' && (
                                <span className="inline-flex items-center px-2 py-1 text-xs font-medium bg-red-100 text-red-800 rounded-full">
                                  Critical
                                </span>
                              )}
                            </div>
                          </div>
                          <button
                            onClick={() => handleRemoveItem(item.id)}
                            className="ml-4 text-red-600 hover:text-red-700"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Add New Item Form */}
            {showAddForm ? (
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                <h4 className="text-sm font-medium text-gray-900 mb-3">Add New Item</h4>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Section *
                    </label>
                    <select
                      value={newItem.section}
                      onChange={(e) => setNewItem({ ...newItem, section: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500"
                    >
                      <option value="">Select section...</option>
                      {sections.map(section => (
                        <option key={section.id} value={section.id}>{section.label}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Checklist Item *
                    </label>
                    <input
                      type="text"
                      value={newItem.text}
                      onChange={(e) => setNewItem({ ...newItem, text: e.target.value })}
                      placeholder="e.g., Review company SMS manual"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Description (optional)
                    </label>
                    <textarea
                      value={newItem.description}
                      onChange={(e) => setNewItem({ ...newItem, description: e.target.value })}
                      placeholder="Additional details or instructions..."
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500"
                      rows={2}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Priority
                    </label>
                    <select
                      value={newItem.priority}
                      onChange={(e) => setNewItem({ ...newItem, priority: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500"
                    >
                      <option value="normal">Normal</option>
                      <option value="critical">Critical (must be completed)</option>
                    </select>
                  </div>

                  <div className="flex justify-end gap-3">
                    <button
                      onClick={() => {
                        setShowAddForm(false);
                        setNewItem({ section: '', text: '', description: '', priority: 'normal' });
                      }}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleAddItem}
                      className="px-4 py-2 text-sm font-medium text-white bg-cyan-600 rounded-md hover:bg-cyan-700"
                    >
                      Add Item
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <button
                onClick={() => setShowAddForm(true)}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-cyan-600 bg-white border border-cyan-600 rounded-md hover:bg-cyan-50"
              >
                <Plus className="h-4 w-4" />
                Add Custom Item
              </button>
            )}
          </div>

          {/* Footer */}
          <div className="bg-gray-50 border-t border-gray-200 px-6 py-4">
            <div className="flex items-center justify-between">
              <p className="text-xs text-gray-500">
                {editingItems.length} custom item{editingItems.length !== 1 ? 's' : ''} configured
              </p>
              <div className="flex items-center gap-3">
                <button
                  onClick={onClose}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  className="px-4 py-2 text-sm font-medium text-white bg-cyan-600 rounded-md hover:bg-cyan-700 flex items-center gap-2"
                >
                  <Save className="h-4 w-4" />
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FamiliarisationSettingsModal;